import { action, observable, reaction, runInAction } from 'mobx';
import { toastr } from 'react-redux-toastr';

import APPConf from 'lib/config/common';
import { activityController } from './activityController';

// Дата-время с бэка: ISO-строка или массив Jackson WRITE_DATES_AS_TIMESTAMPS
// ([год, месяц, день, ч, мин, сек, нанос]) — зависит от инстанса.
export type ClientActivityDateTime = string | number[];

// Устройство клиента (сущность ClientDevice на бэке, агрегированная по device_key)
export interface ClientActivityDevice {
  id: string,
  platform: string,
  deviceModel?: string,
  osName?: string,
  osVersion?: string,
  browserName?: string,
  browserVersion?: string,
  appVersion?: string,
  appBuild?: string,
  lastIpAddress?: string,
  lastSourceDomain?: string,
  country?: string,
  region?: string,
  city?: string,
  seenCount: number,
  firstSeenAt: ClientActivityDateTime,
  lastSeenAt: ClientActivityDateTime,
}

// Единичное событие активности (сущность ClientActivityEvent на бэке)
export interface ClientActivityEvent {
  id: string,
  eventTime: ClientActivityDateTime,
  eventType: string,
  platform: string,
  deviceModel?: string,
  osName?: string,
  osVersion?: string,
  browserName?: string,
  browserVersion?: string,
  appVersion?: string,
  ipAddress?: string,
  country?: string,
  region?: string,
  city?: string,
  sourceDomain?: string,
  widget: boolean,
  crossDomain: boolean,
  utmSource?: string,
  utmMedium?: string,
  utmCampaign?: string,
  requestUri?: string,
  sessionId?: string,
}

// Ответ GET /v1/clients/{clientId}/activity (ClientActivityView на бэке)
export interface ClientActivity {
  clientId: string,
  devices: ClientActivityDevice[],
  events: ClientActivityEvent[],
  page: number,
  size: number,
  total: number,
}

export interface ClientActivityStore {
  activity: ClientActivity,
  activityByClientId: string,
  /** Выборка последних событий (до 500) для дашбордов — не зависит от пагинации таблицы. */
  eventsSample: ClientActivityEvent[],
  eventsSampleByClientId: string,
  loading: boolean,
  error: string,
}

export const DEFAULT_ACTIVITY_PAGE_SIZE = 50;

export const clientActivityStore: ClientActivityStore = observable<ClientActivityStore>({
  activity: null,
  activityByClientId: null,
  eventsSample: [],
  eventsSampleByClientId: null,
  loading: false,
  error: null,
});

export const clientActivityActions = {
  getClientActivity: async (clientId: string, page = 0, size = DEFAULT_ACTIVITY_PAGE_SIZE): Promise<void> => {
    runInAction(() => {
      clientActivityStore.loading = true;
    });
    const response = await activityController.getClientActivity(clientId, { page, size });
    runInAction(() => {
      clientActivityStore.loading = false;
      if (response.errorData) {
        clientActivityStore.error = response.errorData.message;
        return;
      }
      clientActivityStore.activity = response.data;
      clientActivityStore.activityByClientId = clientId;
    });
  },

  // Выборка последних событий для дашбордов: грузится один раз на клиента,
  // пагинация таблицы её не трогает. Ошибка — не тостим (второстепенные данные).
  getEventsSample: async (clientId: string): Promise<void> => {
    runInAction(() => {
      clientActivityStore.eventsSampleByClientId = clientId;
    });
    const response = await activityController.getClientActivity(clientId, { page: 0, size: 500 });
    runInAction(() => {
      if (response.errorData) {
        return;
      }
      clientActivityStore.eventsSample = response.data?.events ?? [];
    });
  },

  clearError: action(() => {
    clientActivityStore.error = null;
  }),
};

reaction(() => clientActivityStore.error, (error) => {
  if (error) {
    toastr.error(APPConf.errorHdr, error);
  }
});
